import { setAuthorizationHeader } from "@/helpers/api_helper";
import { isSSOEnabled } from "@/helpers/tenant";
import { setAuthHeaderSet } from "@/modules/app/appSlice";
import { useAppDispatch, useAppState } from "@/modules/app/useAppContext";
import { getUser, logoutUser, isSessionExpired } from "@/services/auth";
import { useSSOInactivityTimeout } from "@/hooks/useSSOInactivityTimeout";
import { useState, useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Session from "supertokens-auth-react/recipe/session";

const SSOAuthProtected = () => {
  const location = useLocation();
  const user = getUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const appDispatch = useAppDispatch();
  const { checkOnMount } = useSSOInactivityTimeout(!loading && !!user);

  useEffect(() => {
    const checkSession = async () => {
      const sessionExists = await Session.doesSessionExist();
      if (!sessionExists) {
        console.debug("Session does not exist");
        logoutUser();
        navigate("/login", {
          state: { from: location.pathname, expired: true },
        });
        return;
      }

      if (!user) {
        console.debug("Session exists but no user found");
        navigate("/register");
        return;
      }

      // Check if user was inactive too long since last tab close
      const loggedOut = await checkOnMount();
      if (loggedOut) return;

      console.debug("Session exists and user found");
      appDispatch(setAuthHeaderSet(true));
      setAuthorizationHeader(user.auth_token);
      setLoading(false);
    };

    checkSession();
  }, []);

  if (loading) {
    return null;
  }

  return <Outlet />;
};

const BaseAuthProtected = () => {
  const location = useLocation();

  if (isSessionExpired()) {
    logoutUser();
    return <Navigate to="/login" state={{ from: location.pathname, expired: true }} />;
  }

  const user = getUser();
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  return <Outlet />;
};

export const AuthProtected = () => {
  const ssoMode = isSSOEnabled();
  if (ssoMode) {
    return <SSOAuthProtected />;
  }
  return <BaseAuthProtected />;
};

export const TenantProtected = (props: { tenant?: string }) => {
  if (props.tenant === "") {
    return <Navigate to="/coming-soon" />;
  }

  return <Outlet />;
};

/**
 * Layout route gate for Snowflake-only surfaces. Renders nested routes for
 * Snowflake tenants; redirects to /summary (with replace, to avoid back-button
 * traps) for Databricks tenants. Use as `<Route element={<SnowflakeProtected />}>`.
 */
// eslint-disable-next-line react/no-multi-comp
export const SnowflakeProtected = () => {
  const { isDatabricksTenant } = useAppState();
  if (isDatabricksTenant) {
    return <Navigate to="/summary" replace />;
  }
  return <Outlet />;
};

/**
 * Layout route gate for Databricks-only surfaces. Renders nested routes for
 * Databricks tenants; redirects to /summary (with replace) for Snowflake tenants.
 */
// eslint-disable-next-line react/no-multi-comp
export const DatabricksProtected = () => {
  const { isDatabricksTenant } = useAppState();
  if (!isDatabricksTenant) {
    return <Navigate to="/summary" replace />;
  }
  return <Outlet />;
};
